<template>
  <v-row dense>
    <v-col cols="12">
      <v-card class="chart-card" outlined>
        <v-card-title class="py-4">
          <div>
            <h3 class="font-weight-medium mb-0">Distribuição por Zona</h3>
            <p class="caption grey--text mb-1">
              Comparativo de cadastro × apuração por zona eleitoral.
            </p>
            <p class="caption apuracao-nota mb-0">
              <v-icon x-small color="orange darken-2">mdi-information-outline</v-icon>
              <template v-if="metaFiltrada">
                <strong>Apuração total do candidato</strong> — não restrita à meta selecionada.
                A conversão pode exceder 100% em zonas com poucos cadastros nesta meta.
              </template>
              <template v-else>
                Comparativo entre todo o cadastro da equipe e a apuração total do candidato.
              </template>
            </p>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <template v-if="loading">
            <div class="chart-wrapper">
              <v-skeleton-loader type="image"></v-skeleton-loader>
            </div>
          </template>
          <template v-else>
            <v-row dense class="pa-4 chart-block" align="stretch">
              <v-col cols="12" md="7">
                <div class="chart-wrapper">
                  <div v-if="series.length">
                    <apexchart
                      type="donut"
                      :height="chartHeights.donut"
                      :options="zoneChartOptions"
                      :series="series"
                    ></apexchart>
                  </div>
                  <div v-else class="empty-state">
                    Selecione uma equipe para visualizar esta distribuição.
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="5" class="chart-summary">
                <h4 class="subtitle-1 font-weight-medium mb-1">
                  Zonas com maior volume
                </h4>
                <p class="caption grey--text mb-3">
                  Ranking ordenado pela participação percentual.
                </p>
                <v-simple-table dense v-if="topZonasRanking.length" class="ranking-table">
                  <thead>
                    <tr>
                      <th class="summary-rank"></th>
                      <th class="summary-label">Zona</th>
                      <th class="summary-value">Cad.</th>
                      <th class="summary-percent">%</th>
                      <th class="summary-value realizado">Real.</th>
                      <th class="summary-percent realizado">%</th>
                      <th class="summary-conv">Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="zona in topZonasRanking" :key="zona.nome">
                      <td class="summary-rank">
                        {{ zona.posicao }}º
                      </td>
                      <td class="summary-label">
                        {{ zona.nome }}
                      </td>
                      <td class="summary-value">
                        {{ formatNumber(zona.valor) }}
                      </td>
                      <td class="summary-percent">
                        {{ zona.percentual.toFixed(1) }}%
                      </td>
                      <td class="summary-value realizado">
                        {{ formatNumber(zona.realizado) }}
                      </td>
                      <td class="summary-percent realizado">
                        {{ zona.percentualRealizado.toFixed(1) }}%
                      </td>
                      <td class="summary-conv">
                        {{ zona.valor > 0 ? zona.percentualConversao.toFixed(1) + '%' : '—' }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div v-else class="empty-state small">
                  Sem dados suficientes para o ranking no momento.
                </div>
                <div class="text-right mt-3">
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="todasZonasDialog = true"
                  >
                    <v-icon left small>mdi-view-list</v-icon>
                    Ver todas as zonas
                  </v-btn>
                </div>
                <div
                  v-if="outrasZonasResumo"
                  class="summary-others mt-3"
                >
                  <span class="summary-label">{{ outrasZonasResumo.nome }}</span>
                  <span class="summary-value">
                    {{ formatNumber(outrasZonasResumo.valor) }}
                  </span>
                  <span class="summary-percent">
                    {{ outrasZonasResumo.percentual.toFixed(1) }}%
                  </span>
                  <span class="summary-value realizado">
                    {{ formatNumber(outrasZonasResumo.realizado) }}
                  </span>
                  <span class="summary-percent realizado">
                    {{ outrasZonasResumo.percentualRealizado.toFixed(1) }}%
                  </span>
                  <span class="summary-conv">
                    {{ outrasZonasResumo.valor > 0 ? outrasZonasResumo.percentualConversao.toFixed(1) + '%' : '—' }}
                  </span>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <zona-detalhe-modal
        v-model="detalheDialog"
        :titulo="detalheTitulo"
        :items="detalheItems"
        :ranking="detalheRanking"
        :total="detalheTotal"
        :search.sync="detalheSearch"
      />

      <zona-completa-modal
        v-model="todasZonasDialog"
        :zonas="todasZonasRanking"
        :total-cadastrado="totalCadastradoGeral"
        :total-apuracao="totalApuracao"
        :meta-filtrada="metaFiltrada"
      />
    </v-col>
  </v-row>
</template>

<script>
import { formatNumber, formatDateTime } from "./utils/formatters";
import ZonaDetalheModal from "./modals/ZonaDetalheModal.vue";
import ZonaCompletaModal from "./modals/ZonaCompletaModal.vue";

export default {
  name: "DistribuicaoZona",
  components: {
    ZonaDetalheModal,
    ZonaCompletaModal,
  },
  props: {
    series: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
    pessoasMap: {
      type: Object,
      default: () => ({}),
    },
    aggregated: {
      type: Object,
      default: null,
    },
    aggregatedLabels: {
      type: Array,
      default: () => [],
    },
    apuracaoMap: {
      type: Object,
      default: () => ({}),
    },
    totalApuracao: {
      type: Number,
      default: 0,
    },
    metaFiltrada: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chartHeights: {
        donut: 450,
      },
      detalheDialog: false,
      detalheTitulo: "",
      detalheSearch: "",
      detalheItems: [],
      detalheRanking: [],
      detalheTotal: 0,
      todasZonasDialog: false,
    };
  },
  computed: {
    topZonasRanking() {
      const total = this.series.reduce((acc, item) => acc + item, 0) || 1;
      const aggregatedLabel = this.aggregated ? this.aggregated.nome : null;

      const base = this.labels.map((label, index) => ({
        nome: label,
        valor: this.series[index] || 0,
      }));

      const filtered = aggregatedLabel
        ? base.filter((item) => item.nome !== aggregatedLabel)
        : base;

      return filtered
        .sort((a, b) => b.valor - a.valor)
        .slice(0, 5)
        .map((item, index) => {
          const realizado = Number(this.apuracaoMap[item.nome] || 0);
          const percentualRealizado =
            this.totalApuracao > 0 ? (realizado / this.totalApuracao) * 100 : 0;
          const percentualConversao =
            item.valor > 0 ? (realizado / item.valor) * 100 : 0;
          return {
            ...item,
            posicao: index + 1,
            percentual: (item.valor / total) * 100,
            realizado,
            percentualRealizado,
            percentualConversao,
          };
        });
    },
    todasZonasRanking() {
      const aggregatedLabel = this.aggregated ? this.aggregated.nome : null;
      const cadastroMap = {};

      this.labels.forEach((label, idx) => {
        if (label === aggregatedLabel) return;
        cadastroMap[label] = Number(this.series[idx] || 0);
      });

      (this.aggregatedLabels || []).forEach((label) => {
        const pessoas = (this.pessoasMap || {})[label];
        if (pessoas) {
          cadastroMap[label] = pessoas.length;
        } else if (cadastroMap[label] === undefined) {
          cadastroMap[label] = 0;
        }
      });

      const nomes = new Set([
        ...Object.keys(cadastroMap),
        ...Object.keys(this.apuracaoMap || {}),
      ]);

      const totalCadastrado =
        Object.values(cadastroMap).reduce((acc, v) => acc + v, 0) || 0;

      const linhas = Array.from(nomes).map((nome) => {
        const valor = Number(cadastroMap[nome] || 0);
        const realizado = Number((this.apuracaoMap || {})[nome] || 0);
        const percentual =
          totalCadastrado > 0 ? (valor / totalCadastrado) * 100 : 0;
        const percentualRealizado =
          this.totalApuracao > 0 ? (realizado / this.totalApuracao) * 100 : 0;
        const percentualConversao =
          valor > 0 ? (realizado / valor) * 100 : 0;
        return {
          nome,
          valor,
          realizado,
          percentual,
          percentualRealizado,
          percentualConversao,
        };
      });

      linhas.sort((a, b) => {
        if (b.valor !== a.valor) return b.valor - a.valor;
        return b.realizado - a.realizado;
      });

      return linhas.map((item, idx) => ({ ...item, posicao: idx + 1 }));
    },
    totalCadastradoGeral() {
      return this.series.reduce((acc, item) => acc + item, 0);
    },
    outrasZonasResumo() {
      if (!this.aggregated) {
        return null;
      }

      const total = this.series.reduce((acc, item) => acc + item, 0) || 1;
      const realizado = (this.aggregatedLabels || []).reduce(
        (acc, label) => acc + Number(this.apuracaoMap[label] || 0),
        0
      );
      const percentualRealizado =
        this.totalApuracao > 0 ? (realizado / this.totalApuracao) * 100 : 0;
      const percentualConversao =
        this.aggregated.valor > 0
          ? (realizado / this.aggregated.valor) * 100
          : 0;

      return {
        nome: this.aggregated.nome,
        valor: this.aggregated.valor,
        percentual: (this.aggregated.valor / total) * 100,
        realizado,
        percentualRealizado,
        percentualConversao,
      };
    },
    zoneChartOptions() {
      const self = this;
      const totalCadastros = this.series.reduce((acc, item) => acc + item, 0);
      return {
        labels: this.labels,
        chart: {
          toolbar: { show: false },
          events: {
            dataPointSelection(event, chartContext, config) {
              const index = config.dataPointIndex;
              const label = self.labels[index];
              if (label) {
                self.abrirDetalheZona(label);
              }
            },
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "78%",
              labels: {
                show: true,
                name: {
                  fontSize: "13px",
                  offsetY: 8,
                },
                value: {
                  fontSize: "22px",
                  formatter(val) {
                    return self.formatNumber(Number(val));
                  },
                },
                total: {
                  show: true,
                  label: "Total",
                  formatter() {
                    return self.formatNumber(totalCadastros);
                  },
                },
              },
            },
          },
        },
        legend: {
          position: "bottom",
          horizontalAlign: "left",
          itemMargin: {
            horizontal: 16,
            vertical: 6,
          },
          formatter(seriesName, opts) {
            const value = opts.w.globals.series[opts.seriesIndex];
            const percent = opts.w.globals.seriesPercent[opts.seriesIndex][0] || 0;
            return `${seriesName} — ${self.formatNumber(value)} (${percent.toFixed(1)}%)`;
          },
        },
        dataLabels: {
          dropShadow: {
            enabled: false,
          },
          enabled: false,
        },
        tooltip: {
          y: {
            formatter: (val) => `${this.formatNumber(val)} cadastros`,
          },
        },
        theme: {
          palette: "palette4",
        },
      };
    },
  },
  watch: {
    detalheDialog(valor) {
      if (!valor) {
        this.detalheSearch = "";
        this.detalheItems = [];
        this.detalheTitulo = "";
        this.detalheRanking = [];
        this.detalheTotal = 0;
      }
    },
  },
  methods: {
    formatNumber,
    formatDateTime,
    abrirDetalheZona(label) {
      if (!label) {
        return;
      }

      const zonaPessoasMap = this.pessoasMap || {};
      if (!Object.keys(zonaPessoasMap).length) {
        return;
      }

      const normalizedLabel = String(label);
      let items = [];

      if (
        this.aggregated &&
        normalizedLabel === this.aggregated.nome &&
        this.aggregatedLabels.length
      ) {
        this.aggregatedLabels.forEach((zonaLabel) => {
          if (zonaPessoasMap[zonaLabel]) {
            items = items.concat(zonaPessoasMap[zonaLabel]);
          }
        });
      } else if (zonaPessoasMap[normalizedLabel]) {
        items = zonaPessoasMap[normalizedLabel];
      }

      this.detalheItems = items;
      this.detalheRanking = this.calcularRankingAssessores(items);
      this.detalheTotal = items.length;
      this.detalheTitulo = normalizedLabel;
      this.detalheSearch = "";
      this.detalheDialog = true;
    },
    calcularRankingAssessores(items) {
      if (!items || !items.length) {
        return [];
      }

      const total = items.length || 1;
      const mapa = {};

      items.forEach((item) => {
        const assessor = item.assessor || "Não informado";
        mapa[assessor] = (mapa[assessor] || 0) + 1;
      });

      return Object.entries(mapa)
        .map(([nome, qtd]) => ({
          nome,
          total: qtd,
          percentual: (qtd / total) * 100,
        }))
        .sort((a, b) => b.total - a.total)
        .map((item, index) => ({
          ...item,
          posicao: index + 1,
        }));
    },
  },
};
</script>

<style scoped>
.chart-card {
  border-radius: 16px;
  min-height: 140px;
}

.chart-block {
  min-height: 360px;
}

.chart-wrapper {
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-summary {
  display: flex;
  flex-direction: column;
}

.ranking-table {
  font-size: 0.85rem;
}

.ranking-table th {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #757575;
  font-weight: 600;
}

.summary-rank {
  width: 40px;
  font-weight: 600;
  color: #1976d2;
}

.summary-label {
  font-weight: 500;
  white-space: nowrap;
}

.summary-value {
  text-align: right;
  font-weight: 500;
}

.summary-percent {
  text-align: right;
  font-size: 0.8rem;
  color: #616161;
  width: 50px;
}

.summary-value.realizado,
.summary-percent.realizado {
  color: #2e7d32;
}

.summary-conv {
  text-align: right;
  font-size: 0.8rem;
  color: #6a1b9a;
  width: 60px;
  white-space: nowrap;
}

.summary-others {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background-color: #eceff1;
  border-radius: 8px;
  font-size: 0.85rem;
}

.apuracao-nota {
  color: #ef6c00;
  font-size: 0.72rem !important;
  line-height: 1.35;
}

.empty-state {
  text-align: center;
  max-width: 320px;
  line-height: 1.4;
  color: #757575;
}

.empty-state.small {
  max-width: none;
  text-align: left;
}

@media (max-width: 960px) {
  .chart-card {
    min-height: 320px;
  }
  .chart-block {
    min-height: 320px;
  }
  .chart-wrapper {
    min-height: 280px;
  }
}
</style>
